import Router from 'next/router';
import { memo, useCallback, useMemo } from 'react';
import { useMount } from 'react-use';

import { useTrackClickedUpgradeButton, useTrackPlanUpgradeModalClicked } from '@air/analytics';
import { Check as CheckIcon, CreditIcon } from '@air/next-icons';
import { Badge } from '@air/primitive-badge';
import { Button, type ButtonProps } from '@air/primitive-button';
import { useAirModal, useModalContext } from '@air/provider-modal';
import { cn } from '@air/utils-tailwind';

import { DemoFormModal } from '~/components/Modals/DemoFormModal';
import { PlanChangeConfirmationModal } from '~/components/Modals/PlansModal/PlanChangeConfirmationModal';
import {
  formatMonthlyPrice,
  type PlanInfoType,
  type UpgradePlanModalBillingCycle,
} from '~/components/Modals/PlansModal/utils';
import { Routes } from '~/constants/routes';
import { useSubscriptionContext } from '~/providers/SubscriptionProvider';

export type UpgradePlanModalPlanCardProps = PlanInfoType & {
  currentPlan: PlanInfoType | null;
  billingCycle: UpgradePlanModalBillingCycle;
  onClose: () => void;
};

export const UpgradePlanModalPlanCard = memo(
  ({
    planId,
    planTitle,
    action,
    prices,
    tagline,
    pricingModel,
    credits,
    options,
    customPriceDescription,
    isRecommended,
    secondaryCta,
    previousPlan,
    stripePlanIds,
    billingCycle,
    currentPlan,
    onClose,
  }: UpgradePlanModalPlanCardProps) => {
    const { trackClickedUpgradeButton } = useTrackClickedUpgradeButton();
    const { trackPlanUpgradeModalClicked } = useTrackPlanUpgradeModalClicked();
    const { dispatch: modalDispatch } = useModalContext();
    const [showDemoFormModal] = useAirModal(DemoFormModal);
    const [showPlanChangeConfirmationModal] = useAirModal(PlanChangeConfirmationModal);
    const { data: subscription } = useSubscriptionContext();

    useMount(() => {
      if (action === 'self-service-upgrade-trial-via-checkout' || action === 'self-service-upgrade-via-checkout') {
        Router.prefetch(Routes.workspaceAdmin.checkout);
      }
    });

    const onSelfServiceDowngrade = useCallback(() => {
      showPlanChangeConfirmationModal({
        action: 'self-service-downgrade',
        planId,
        planTitle,
        stripePlanIds,
        prices,
        pricingModel,
      });
    }, [showPlanChangeConfirmationModal, planId, planTitle, prices, pricingModel, stripePlanIds]);

    const onBookDemo = useCallback(() => {
      showDemoFormModal({ trigger: planId === 'enterprise' ? 'plans_modal_enterprise' : 'plans_modal_business' });
    }, [planId, showDemoFormModal]);

    const navigateToCheckout = useCallback(() => {
      onClose();

      /**
       * Since the plan modal generally overlaps another modal, only the highest level modal is closed.
       * Therefore we need to close all modals with the ModalContext before redirecting to checkout.
       */
      modalDispatch({ type: 'hide-all' });

      Router.push({
        pathname: Routes.workspaceAdmin.checkout,
        search: `planId=${planId}`,
      });
    }, [planId, modalDispatch, onClose]);

    const onSelfServiceUpgrade = useCallback(() => {
      trackClickedUpgradeButton({ plan: planId });
      if (action === 'self-service-upgrade-via-checkout') {
        navigateToCheckout();
      } else {
        trackPlanUpgradeModalClicked({
          old_plan: subscription?.plan ?? 'basic',
          new_plan: planId,
        });
        showPlanChangeConfirmationModal({
          action: 'self-service-upgrade',
          planId,
          planTitle,
          stripePlanIds,
          prices,
          pricingModel,
        });
      }
    }, [
      action,
      navigateToCheckout,
      planId,
      subscription?.plan,
      trackClickedUpgradeButton,
      trackPlanUpgradeModalClicked,
      showPlanChangeConfirmationModal,
      planTitle,
      prices,
      pricingModel,
      stripePlanIds,
    ]);

    const onStartFreeTrial = useCallback(() => {
      trackClickedUpgradeButton({ plan: planId });
      if (action === 'self-service-upgrade-trial-via-checkout') {
        navigateToCheckout();
      } else {
        trackPlanUpgradeModalClicked({
          old_plan: subscription?.plan ?? 'basic',
          new_plan: planId,
        });
        showPlanChangeConfirmationModal({
          action: 'self-service-upgrade-trial',
          planId,
          planTitle,
          stripePlanIds,
          prices,
          pricingModel,
        });
      }
    }, [
      action,
      navigateToCheckout,
      planId,
      subscription?.plan,
      trackClickedUpgradeButton,
      trackPlanUpgradeModalClicked,
      showPlanChangeConfirmationModal,
      planTitle,
      prices,
      pricingModel,
      stripePlanIds,
    ]);

    const buttonProps = useMemo<ButtonProps>(() => {
      switch (action) {
        case 'current-plan':
          return {
            children: 'Current Plan',
            disabled: true,
          };
        case 'self-service-downgrade':
          return {
            children: `Switch to ${planTitle}`,
            onClick: onSelfServiceDowngrade,
            color: 'grey',
          };
        case 'self-service-upgrade-trial':
        case 'self-service-upgrade-trial-via-checkout':
          return {
            children: 'Start Free Trial',
            onClick: onStartFreeTrial,
          };
        case 'self-service-upgrade':
        case 'self-service-upgrade-via-checkout':
          return {
            children: currentPlan?.planId === 'basic' ? 'Get started' : `Switch to ${planTitle}`,
            onClick: onSelfServiceUpgrade,
          };
        case 'book-demo':
          return {
            children: 'Contact sales',
            onClick: onBookDemo,
            appearance: 'outline',
          };
      }
    }, [
      action,
      planTitle,
      onBookDemo,
      onSelfServiceDowngrade,
      onSelfServiceUpgrade,
      onStartFreeTrial,
      currentPlan?.planId,
    ]);

    return (
      <div
        data-testid="PLAN_CONTAINER"
        data-plan={planId}
        className={cn(
          'relative flex w-full max-w-md flex-col gap-6 rounded-12 border border-gray-4 bg-gray-1 p-6',
          action === 'self-service-downgrade' && 'bg-gray-2',
        )}
      >
        {isRecommended && (
          <Badge color="light-blue" className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2">
            Most popular
          </Badge>
        )}

        <div>
          <h3 className="font-control text-36 text-gray-12">{planTitle}</h3>
          {tagline && <p className="text-14 text-gray-10">{tagline}</p>}
        </div>

        <div className="flex flex-col gap-4">
          {customPriceDescription ? (
            <p className="font-semibold text-36 text-gray-12 leading-none">{customPriceDescription}</p>
          ) : (
            <div className="flex items-end gap-2">
              <span className="font-semibold text-36 text-gray-12 leading-none">
                {formatMonthlyPrice({ prices, billingCycle })}
              </span>{' '}
              <div className="flex flex-col font-semibold text-12 text-gray-9">
                <span>per month</span>
                <span>billed {billingCycle === 'annual' ? 'annually' : 'monthly'}</span>
              </div>
            </div>
          )}
          <Button
            data-testid="UPGRADE_PLAN_MODAL_CARD_BUTTON"
            data-plan={planId}
            data-action={action}
            size="large"
            color="blue"
            appearance="filled"
            {...buttonProps}
          />
        </div>

        <div className="flex flex-col gap-3 text-14">
          {!!credits && (
            <div className="flex items-start gap-2">
              <CreditIcon className="mt-0.5 size-4 flex-shrink-0 text-amber-9" />
              <div className="font-semibold text-gray-10">{credits}</div>
            </div>
          )}
          {!!options && (
            <div className="flex flex-col gap-2">
              <p className="font-medium text-gray-12">
                {previousPlan ? `Everything in ${previousPlan.planTitle}, plus:` : 'All plans include:'}
              </p>
              {options.map((option, index) => (
                <div className="flex items-start gap-2" key={index}>
                  <CheckIcon className="mt-0.5 size-4 flex-shrink-0 text-grass-9" />
                  <div className="text-gray-10">{option}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {secondaryCta === 'book-demo' && (
          <div className="mt-auto flex justify-center">
            <Button type="button" appearance="outline" color="grey" size="medium" onClick={onBookDemo}>
              Get a demo
            </Button>
          </div>
        )}
      </div>
    );
  },
);

UpgradePlanModalPlanCard.displayName = 'UpgradePlanModalPlanCard';
